var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import { useOkapiKy } from "@folio/stripes/core";
import { fetchPreviousAuditLog } from "../../api/auditService";
import { computeAuditDiff, formatAuditValue, parseAuditData, } from "../../utils/auditLogDiff";
import { formatDateTime } from "../../utils/dateTimeUtil";
var cellStyle = {
    padding: "0.25rem 0.5rem",
    verticalAlign: "top",
    wordBreak: "break-word",
    textAlign: "left",
};
/**
 * The path cell keeps every path segment whole (no mid-word breaks, so its minimum width is the longest segment)
 * and may wrap only before a "." or "[".
 */
var pathCellStyle = __assign(__assign({}, cellStyle), { wordBreak: "normal", overflowWrap: "break-word" });
/** Splits a technical path before each "." and "[" and allows a line break there. */
export var renderPathWithBreaks = function (path) {
    return path.split(/(?=[.[])/).map(function (segment, index) { return (
    // eslint-disable-next-line react/no-array-index-key
    React.createElement(React.Fragment, { key: index },
        index > 0 && React.createElement("wbr", null),
        segment)); });
};
/**
 * "Změny" section of an update record: the fields that changed against the previous audit record of the same
 * entity (RECEK-75765).
 */
var AuditLogChanges = function (_a) {
    var auditLog = _a.auditLog;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var _b = useQuery(["audit-log-previous", auditLog.id], function () { return fetchPreviousAuditLog(ky, auditLog.id); }, { retry: false }), previous = _b.data, isLoading = _b.isLoading, isError = _b.isError;
    var message = function (id) { return (React.createElement("p", null, formatMessage({ id: "ui-br-users.auditLogs.changes.".concat(id) }))); };
    if (isLoading)
        return message("loading");
    if (isError)
        return message("loadError");
    if (!previous)
        return message("noPrevious");
    var previousData = parseAuditData(previous.data);
    var currentData = parseAuditData(auditLog.data);
    if (previousData === undefined || currentData === undefined) {
        return message("invalidData");
    }
    var changes = computeAuditDiff(previousData, currentData);
    return (React.createElement(React.Fragment, null,
        React.createElement("p", null, formatMessage({ id: "ui-br-users.auditLogs.changes.comparedWith" }, { time: formatDateTime(previous.createTime) })),
        changes.length === 0 ? (message("none")) : (React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", { style: cellStyle }, formatMessage({
                        id: "ui-br-users.auditLogs.changes.column.path",
                    })),
                    React.createElement("th", { style: cellStyle }, formatMessage({
                        id: "ui-br-users.auditLogs.changes.column.oldValue",
                    })),
                    React.createElement("th", { style: cellStyle }, formatMessage({
                        id: "ui-br-users.auditLogs.changes.column.newValue",
                    })))),
            React.createElement("tbody", null, changes.map(function (change, index) { return (
            // eslint-disable-next-line react/no-array-index-key
            React.createElement("tr", { key: "".concat(change.path, "-").concat(index) },
                React.createElement("td", { style: pathCellStyle },
                    React.createElement("code", null, renderPathWithBreaks(change.path)),
                    change.kind !== "changed" &&
                        " (".concat(formatMessage({
                            id: "ui-br-users.auditLogs.changes.kind.".concat(change.kind),
                        }), ")")),
                React.createElement("td", { style: cellStyle }, formatAuditValue(change.oldValue)),
                React.createElement("td", { style: cellStyle }, formatAuditValue(change.newValue)))); }))))));
};
export default AuditLogChanges;
